import { SurchargeSettings } from '@/common/application/types/CompanyTypes';
import { parseNumeric } from '@/common/application/utils/numberUtils';

export type AchSurchargeConfig = Pick<
  SurchargeSettings,
  'achSurcharge' | 'achSurchargeCap' | 'achSurchargeIsVariable'
>;

/**
 * Parse an AppDefault boolean-ish value (boolean, "true"/"false"/"1"/"0", or missing) into a bool,
 * falling back to `defaultValue` when the row is absent or the payload isn't recognizable.
 */
const parseAppDefaultBool = (value: unknown, defaultValue: boolean): boolean => {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }

  return defaultValue;
};

/** AppDefault ACHSurchargeIsVariable when present; false when absent (e.g., DB not migrated yet for PMTS-40). */
export const getAchSurchargeIsVariable = (value: unknown): boolean =>
  parseAppDefaultBool(value, false);

/**
 * AppDefault enabled/disabled toggle (e.g. CreditCardEnabled, ACHEnabled). A missing row means the
 * payment method was never disabled, so it defaults to enabled — mirrors the Merchant Portal's default.
 */
export const getAppDefaultEnabledFlag = (value: unknown): boolean =>
  parseAppDefaultBool(value, true);

/** ACH fee in dollars: percentage of amount when variable, flat otherwise (legacy cap fallback). */
export const calculateAchFee = (amount: number, config: AchSurchargeConfig): number => {
  const achSurcharge = parseNumeric(config.achSurcharge);
  const legacyCap = parseNumeric(config.achSurchargeCap);
  const isVariable = getAchSurchargeIsVariable(config.achSurchargeIsVariable);

  if (isVariable) {
    if (achSurcharge <= 0) {
      return 0;
    }

    return amount * (achSurcharge / 100);
  }

  if (achSurcharge > 0) {
    return achSurcharge;
  }

  return legacyCap;
};

/**
 * Configured ACH surcharge for terms placeholders, with its unit:
 * "1.00%" when variable (percentage), "$15.00" when flat (legacy cap fallback).
 * The unit lives here so the disclaimer wording no longer hardcodes a "%" after the token.
 */
export const formatAchSurchargeConfigForTerms = (config: AchSurchargeConfig): string => {
  const achSurcharge = parseNumeric(config.achSurcharge);
  const legacyCap = parseNumeric(config.achSurchargeCap);
  const isVariable = getAchSurchargeIsVariable(config.achSurchargeIsVariable);

  if (isVariable) {
    return `${(achSurcharge > 0 ? achSurcharge : 0).toFixed(2)}%`;
  }

  const flat = achSurcharge > 0 ? achSurcharge : legacyCap;
  return `$${(flat > 0 ? flat : 0).toFixed(2)}`;
};

/** Left-column fee label for confirmation / history detail rows. ACH uses legal wording. */
export const getPaymentFeeLabel = (method?: string | null): string => {
  if (!method?.trim()) {
    return 'Fee';
  }

  const normalized = method.trim().toLowerCase();
  if (
    normalized === 'ach' ||
    normalized.includes('electronic check') ||
    normalized.includes('e-check') ||
    normalized === 'echeck'
  ) {
    return 'Electronic Check Processing Fee';
  }

  return `${method.trim()} Fee`;
};
